import css from './Base.module.scss'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { QRCodeSVG } from 'qrcode.react'

import DataTestId from 'meta/dataTestId'
import { IUserTicketData } from 'meta/pages/v4/ticket'
import { ITypeTicketData } from 'meta/pages/v4/typeticket'
import { ITypeTicketCollectionData } from 'meta/pages/v4/typeticketcollection'
import { IWalletEvent } from 'meta/pages/v4/wallet'

import Img from 'components/Media/Img'
import { useV4TicketStatus } from 'components/Pages/Wallet/hooks/useTicketStatus'
import { useUrlCloudfront } from 'hooks/useEnv'

type Props = {
  show: boolean
  ticketData: IUserTicketData
  typeTicketData: ITypeTicketData
  typeTicketCollectionData?: ITypeTicketCollectionData
  walletEventData: IWalletEvent
  setShowAddExtraBalloon(value: boolean): void
  useLongCard: boolean
}

const TicketCardBase: React.FC<Props> = (props) => {
  const {
    show,
    ticketData,
    typeTicketData,
    typeTicketCollectionData,
    walletEventData,
    setShowAddExtraBalloon,
    useLongCard,
  } = props

  const { t } = useTranslation()

  const urlCloudfront = useUrlCloudfront()

  const statusSplit = useMemo(
    () => ticketData.status.label && ticketData.status.label.split('-'),
    [ticketData.status.label]
  )
  const statusHeader = useMemo(() => statusSplit && statusSplit[0], [statusSplit])

  const status = useV4TicketStatus({ userTicketData: ticketData, walletEventData })

  const hasMapData = ticketData.mapData?.row && ticketData.mapData?.seat && ticketData.mapData?.section

  const getTicketStatusLabel = (statusKey: string) => {
    switch (statusKey) {
      case 'black_list':
      case 'black_list_resale':
        return 'blackList'
      case 'sport_banned':
        return 'sportBanned'
      default:
        return statusKey
    }
  }

  return (
    <>
      <div className={classNames(css.walletTicketCard__base, { [css.long]: useLongCard })}>
        <div className={classNames(css.walletTicketCard__qrCode, { [css.walletTicketCard__hidden]: !show })}>
          {ticketData.secret ? (
            <QRCodeSVG data-testid={DataTestId.wallet.canvasSquareQR} value={ticketData.secret} size={173} />
          ) : (
            <Img src={`/wallet/locked_qr_code.jpg`} />
          )}
        </div>

        <div className={css.walletTicketCard__info}>
          {
            <>
              <h2>
                {ticketData.firstName && ticketData.lastName ? `${ticketData.firstName} ${ticketData.lastName}` : '-'}
              </h2>
              <div className={css.sep} />
            </>
          }
          <h5>
            {typeTicketData.fiscalData?.typeTicket && !ticketData.seasonData && (
              <span>{typeTicketData.fiscalData?.typeTicket}</span>
            )}

            {typeTicketData.fiscalData?.typeTicketDescription &&
              !typeTicketData.fiscalData?.typeTicketDescription?.includes('-') &&
              !ticketData.seasonData &&
              ` - `}

            {typeTicketData.fiscalData?.typeTicketDescription && (
              <span>{typeTicketData.fiscalData?.typeTicketDescription}</span>
            )}

            {typeTicketData.fiscalData?.typeTicketDescription &&
              typeTicketCollectionData?.name &&
              !typeTicketData.fiscalData?.typeTicketDescription.includes('-') &&
              ` - `}

            {typeTicketCollectionData?.name && <span>{`${typeTicketCollectionData.name}`}</span>}
            {typeTicketData.notes &&
              !typeTicketData.fiscalData?.typeTicketDescription?.includes(typeTicketData.notes) && (
                <span>{`${typeTicketData.notes}`}</span>
              )}
          </h5>

          {typeTicketData.sector?.name && <h5>{t('wallet:sector', { sectorName: typeTicketData.sector.name })}</h5>}

          {hasMapData && <h5>{t('wallet:row', { row: ticketData.mapData.row })}</h5>}
          {hasMapData && <h5>{t('wallet:seat', { seat: ticketData.mapData.seat })}</h5>}
          {hasMapData && <h5>{t('wallet:section', { section: ticketData.mapData.section })}</h5>}

          {ticketData.ticketMore &&
            ticketData.ticketMore.map((more) => (
              <React.Fragment key={more.field}>
                <div className={css.sep} />
                <div style={{ display: 'flex' }}>
                  <h5>{more.field}:&nbsp;</h5>
                  <div>{more.value}</div>
                </div>
              </React.Fragment>
            ))}
        </div>

        {/* {ticketData.seasonData && <div>{t('wallet:isSeasonTicket')}</div>} */}

        {useLongCard && (
          <div className={css.walletTicketCard__addExtra} onClick={() => setShowAddExtraBalloon(true)}>
            <div className={css.button}>
              <span className={css.addExtra_title}>{t('wallet:addExtra')}</span>
            </div>
          </div>
        )}
      </div>

      <div
        className={classNames(
          css.walletTicketCard__status,
          css[getTicketStatusLabel(ticketData.status.key.toLowerCase())],
          {
            [css.walletTicketCard__hidden]: !show,
            [css.long]: useLongCard,
          }
        )}
        style={{
          backgroundImage: `url(${urlCloudfront}/images/wallet/status_${getTicketStatusLabel(ticketData.status.key.toLowerCase())}.svg)`,
        }}
      >
        <h3>{statusHeader}</h3>
        <div data-testid={DataTestId.wallet.ticketStatus}>{status}</div>
      </div>
    </>
  )
}

export default TicketCardBase
